import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Printer, Receipt } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, dateTime, humanize, money } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { DataTable, FilterField, PageHeader, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { VisitOrderLinesCard } from './VisitOrderLinesCard';

interface Subtotals {
  invoiceCount: number;
  grossMinor: number;
  discountMinor: number;
  taxMinor: number;
  reductionsMinor: number;
  netMinor: number;
  panelShareMinor: number;
  patientShareMinor: number;
  paidMinor: number;
  balanceMinor: number;
}

interface StatementInvoice {
  id: string;
  number: string;
  status: string;
  date: string;
  payerName: string | null;
  grossMinor: number;
  discountMinor: number;
  netMinor: number;
  panelShareMinor: number;
  patientShareMinor: number;
  paidMinor: number;
  balanceMinor: number;
}

interface Statement {
  scope: {
    kind: 'encounter' | 'admission' | 'patient';
    admissionNumber: string | null;
    admittedAt: string | null;
    dischargedAt: string | null;
    encounterStartedAt: string | null;
  };
  patient: { id: string; mrn: string; fullName: string } | null;
  generatedAt: string;
  truncated: boolean;
  limit: number;
  groups: { context: string; label: string; invoices: StatementInvoice[]; subtotals: Subtotals }[];
  payments: { id: string; invoiceNumber: string | null; receiptNo: string | null; at: string; mode: string; amountMinor: number }[];
  refunds: { id: string; invoiceNumber: string | null; receiptNo: string | null; at: string; amountMinor: number; reason: string }[];
  deposits: { id: string; receiptNo: string | null; at: string; type: string; mode: string; amountMinor: number }[];
  totals: Subtotals & { paymentsMinor: number; refundedMinor: number; depositsHeldMinor: number; netPositionMinor: number };
}

export function StatementPage() {
  const [params] = useSearchParams();
  const toast = useToast();
  const patientId = params.get('patientId') ?? undefined;
  const encounterId = params.get('encounterId') ?? undefined;
  const admissionId = params.get('admissionId') ?? undefined;
  const [from, setFrom] = useState(params.get('from') ?? '');
  const [to, setTo] = useState(params.get('to') ?? '');
  const scopeParams = { patientId, encounterId, admissionId, from, to };

  const statement = useQuery({
    queryKey: ['statement', patientId, encounterId, admissionId, from, to],
    queryFn: () => get<Statement>(`/statements${qs(scopeParams)}`),
    enabled: Boolean(patientId || encounterId || admissionId),
  });

  const print = useMutation({
    mutationFn: (size: 'A4' | 'thermal') => get<{ html: string }>(`/statements/print${qs({ ...scopeParams, size })}`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('billing', 'could_not_print'), describeError(error)),
  });

  if (!patientId && !encounterId && !admissionId) {
    return <Alert variant="destructive">{tr('billing', 'open_a_statement_from_a_patient_a_visit_or_an')}</Alert>;
  }

  const s = statement.data;
  const title =
    s?.scope.kind === 'admission' ? `Statement · admission ${s.scope.admissionNumber ?? ''}` : s?.scope.kind === 'encounter' ? 'Visit statement' : 'Statement of account';

  return (
    <>
      <PageHeader
        title={title}
        description={s?.patient ? `${s.patient.fullName} · ${s.patient.mrn}` : undefined}
        actions={
          s?.patient && (
            <Link to={`/patients/${s.patient.id}`}>
              <Button variant="outline">{tr('billing', 'patient_record')}</Button>
            </Link>
          )
        }
      />

      <SearchFilterBar
        filters={
          !encounterId &&
          !admissionId && (
            <>
              <FilterField label={tr('billing', 'from_date')}>
                <DatePicker value={from} onChange={setFrom} className="w-40" />
              </FilterField>
              <FilterField label={tr('billing', 'to_date')}>
                <DatePicker value={to} onChange={setTo} className="w-40" />
              </FilterField>
            </>
          )
        }
        trailing={
          <>
            <Button variant="outline" size="sm" onClick={() => print.mutate('thermal')} loading={print.isPending}>
              <Receipt className="h-4 w-4" /> {tr('billing', 'thermal')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => print.mutate('A4')} loading={print.isPending}>
              <Printer className="h-4 w-4" /> {tr('billing', 'print_a4')}
            </Button>
          </>
        }
        onReset={
          from || to
            ? () => {
                setFrom('');
                setTo('');
              }
            : undefined
        }
      />

      {statement.isLoading && <p className="text-sm text-muted-foreground">{tr('billing', 'gathering_every_bill')}</p>}
      {statement.error && <Alert variant="destructive">{describeError(statement.error)}</Alert>}

      {s && (
        <>
          {s.truncated && (
            <Alert variant="warning" className="mb-3">
              {tr('billing', 'this_statement_shows_the_latest')} {s.limit} {tr('billing', 'invoices_only_narrow_the_dates_to_see_the_rest')}
            </Alert>
          )}

          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label={tr('billing', 'total_billed')} value={money(s.totals.netMinor)} sub={tr('billing', 'x_invoice_s', { invoiceCount: s.totals.invoiceCount })} />
            <StatTile label={tr('billing', 'panel_share')} value={money(s.totals.panelShareMinor)} sub={tr('billing', 'patient_share_x', { patientShareMinor: money(s.totals.patientShareMinor) })} />
            <StatTile label={tr('billing', 'paid')} value={money(s.totals.paidMinor)} sub={s.totals.refundedMinor > 0 ? tr('billing', 'x_refunded', { refundedMinor: money(s.totals.refundedMinor) }) : undefined} />
            <StatTile
              label={s.totals.netPositionMinor > 0 ? tr('billing', 'amount_due') : tr('billing', 'credit_refundable')}
              value={money(Math.abs(s.totals.netPositionMinor))}
              sub={s.totals.depositsHeldMinor !== 0 ? tr('billing', 'after_x_advance_held', { depositsHeldMinor: money(s.totals.depositsHeldMinor) }) : undefined}
              tone={s.totals.netPositionMinor > 0 ? 'destructive' : 'success'}
            />
          </div>

          {s.groups.length === 0 && <Alert>{tr('billing', 'no_bills_in_this_scope')}</Alert>}
          {encounterId && <VisitOrderLinesCard encounterId={encounterId} />}

          <div className="space-y-4">
            {s.groups.map((group) => (
              <Card key={group.context}>
                <CardHeader className="flex-row items-center justify-between">
                  <CardTitle className="text-sm">{group.label}</CardTitle>
                  <span className="tabular font-medium">{money(group.subtotals.netMinor)}</span>
                </CardHeader>
                <CardContent>
                  <DataTable
                    dense
                    rows={group.invoices}
                    rowKey={(row) => row.id}
                    columns={[
                      {
                        header: tr('billing', 'invoice'),
                        sort: (row) => row.number,
                        cell: (row) => (
                          <div>
                            <Link className="underline" to={`/billing/invoices/${row.id}`}>
                              {row.number}
                            </Link>
                            <p className="text-xs text-muted-foreground">{dateOnly(row.date)}{row.payerName ? ` · ${row.payerName}` : ''}</p>
                          </div>
                        ),
                      },
                      { header: tr('billing', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
                      { header: tr('billing', 'gross'), align: 'right', sort: (row) => row.grossMinor, cell: (row) => money(row.grossMinor) },
                      { header: tr('billing', 'discount'), align: 'right', sort: (row) => row.discountMinor, cell: (row) => (row.discountMinor ? money(row.discountMinor) : '—') },
                      { header: tr('billing', 'net'), align: 'right', sort: (row) => row.netMinor, cell: (row) => money(row.netMinor) },
                      { header: tr('billing', 'panel'), align: 'right', sort: (row) => row.panelShareMinor, cell: (row) => (row.panelShareMinor ? money(row.panelShareMinor) : '—') },
                      { header: tr('billing', 'paid'), align: 'right', sort: (row) => row.paidMinor, cell: (row) => money(row.paidMinor) },
                      {
                        header: tr('billing', 'balance'),
                        align: 'right',
                        sort: (row) => row.balanceMinor,
                        cell: (row) => (
                          <span className={row.balanceMinor > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground'}>
                            {money(row.balanceMinor)}
                          </span>
                        ),
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{tr('billing', 'payments_and_refunds')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  dense
                  rows={[
                    ...s.payments.map((p) => ({ key: p.id, at: p.at, receiptNo: p.receiptNo, invoiceNumber: p.invoiceNumber, label: humanize(p.mode), amountMinor: p.amountMinor, refund: false })),
                    ...s.refunds.map((r) => ({ key: r.id, at: r.at, receiptNo: r.receiptNo, invoiceNumber: r.invoiceNumber, label: tr('billing', 'refund'), amountMinor: r.amountMinor, refund: true })),
                  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())}
                  rowKey={(row) => row.key}
                  empty={tr('billing', 'nothing_paid_yet')}
                  columns={[
                    { header: tr('billing', 'when'), sort: (row) => row.at, cell: (row) => dateTime(row.at) },
                    { header: tr('billing', 'receipt'), sort: (row) => row.receiptNo, cell: (row) => <span className="tabular text-xs">{row.receiptNo ?? '—'}</span> },
                    { header: tr('billing', 'invoice'), sort: (row) => row.invoiceNumber, cell: (row) => row.invoiceNumber ?? '' },
                    { header: tr('billing', 'mode'), sort: (row) => row.label, cell: (row) => row.label },
                    {
                      header: tr('billing', 'amount'),
                      align: 'right',
                      sort: (row) => row.amountMinor,
                      cell: (row) => (
                        <span className={row.refund ? 'text-destructive' : undefined}>
                          {row.refund ? '− ' : ''}
                          {money(row.amountMinor)}
                        </span>
                      ),
                    },
                  ]}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{tr('billing', 'advances')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  dense
                  rows={s.deposits}
                  rowKey={(row) => row.id}
                  empty={tr('billing', 'no_advances')}
                  columns={[
                    { header: tr('billing', 'when'), sort: (row) => row.at, cell: (row) => dateTime(row.at) },
                    { header: tr('billing', 'receipt'), sort: (row) => row.receiptNo, cell: (row) => <span className="tabular text-xs">{row.receiptNo ?? '—'}</span> },
                    { header: tr('billing', 'type'), sort: (row) => row.type, cell: (row) => humanize(row.type) },
                    { header: tr('billing', 'mode'), sort: (row) => row.mode, cell: (row) => humanize(row.mode) },
                    { header: tr('billing', 'amount'), align: 'right', sort: (row) => row.amountMinor, cell: (row) => money(row.amountMinor) },
                  ]}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
